import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { useAuthActions, useConvexAuth } from "@convex-dev/auth/react";
import { Link, Navigate } from "react-router";
import { api } from "@todotogether/backend/_generated/api";
import type { Id } from "@todotogether/backend/_generated/dataModel";
import type { JoinedListSummary, ListSummary, Tier } from "@todotogether/shared";
import { GENERIC_ERROR_MESSAGE, LIST_NAME_MAX, PAID_RESTORE_MESSAGE, SEE_PLANS_LINK_LABEL, capitaliseFirstLetter, dueInfo, errorMessage, listScope, progressFraction, starredFirst } from "@todotogether/shared";

/**
 * Lists screen (ticket #10): the caller's lists in two membership
 * sections, create (Free-Quota enforced server-side), rename (Owner),
 * soft-delete (Owner/Admin), and the Owner's 30-day restore bin.
 * Restore itself is Paid-gated server-side (ticket #71): the bin
 * mirrors that gate for UX — Free Users see an upsell where the
 * Restore button would be.
 *
 * The sections are Personal and Shared (ticket #754), classified by
 * the ONE shared home (listScope, #753) over each row's live
 * memberCount: Personal is an owned list with nobody else on it;
 * Shared is everything else — owned lists with other members AND
 * lists other people own, together. The classification rides the
 * reactive myLists query, so a row moves sections live as membership
 * changes. Row content does not change across a move: the row menu
 * keys off the row's own ownership (the caller's owner role), never
 * the section, so an owned list keeps Rename/Delete after moving to
 * Shared and a joined row never gains it.
 */
export default function App() {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const me = useQuery(api.users.me);
  const myLists = useQuery(api.lists.myLists);
  const deletedLists = useQuery(api.lists.myDeletedLists);
  const entitlements = useQuery(api.entitlements.getMine);

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-paper">
        <p className="text-muted" data-testid="app-loading">
          Loading…
        </p>
      </main>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  return (
    <main className="min-h-screen bg-paper px-6 py-10">
      <div className="mx-auto max-w-2xl">
        {/* Phone-width law (#198): flex-wrap lets the identity keep its
            full line when the actions can't fit beside it — they drop to
            a second, right-aligned row (ml-auto is a no-op while the two
            groups share one row). The identity wraps between words first;
            break-words lets the unbreakable email token break as a last
            resort instead of overflowing, and the action group never
            shrinks. */}
        <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-ink">Your lists</h1>
            {me && (
              <p
                className="mt-1 break-words text-sm text-muted"
                data-testid="viewer-email"
              >
                Signed in as {me.email}
              </p>
            )}
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-3">
            <Link
              to="/app/calendar"
              className="text-sm text-muted hover:text-ink hover:underline"
              data-testid="calendar-link"
            >
              Calendar
            </Link>
            <Link
              to="/app/settings"
              className="text-sm text-muted hover:text-ink hover:underline"
              data-testid="settings-link"
            >
              Settings
            </Link>
            <SignOutButton />
          </div>
        </header>

        <GlobalSearch />
        <NextUp />
        <CreateList />

        {myLists === undefined ||
        myLists === null ||
        deletedLists === undefined ||
        deletedLists === null ? null : (
          <>
            <ListSections owned={myLists.owned} joined={myLists.joined} />
            {deletedLists.length > 0 && (
              <DeletedBin lists={deletedLists} tier={entitlements?.tier} />
            )}
          </>
        )}
      </div>
    </main>
  );
}

/**
 * "Next up" (due dates): the viewer's soonest due items across ALL
 * their lists — overdue first, then upcoming (items:nextDue walks the
 * by_dueAt index ascending). Rows link into the owning list; the panel
 * stays hidden entirely when nothing has a due date.
 */
function NextUp() {
  const nextDue = useQuery(api.items.nextDue);
  if (nextDue === undefined || nextDue === null || nextDue.length === 0) {
    return null;
  }
  return (
    <section
      className="mt-4 rounded-xl border border-line bg-page p-4 shadow-sm"
      data-testid="next-due"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
        Next due
      </h2>
      <ul className="mt-2 space-y-1.5">
        {nextDue.map((n) => {
          const due = dueInfo(n.dueAt);
          // The panel card is a sheet of page, so the tones render as
          // plain text on it — the item-line voice, and every pairing
          // holds AA (the wash pill put danger at 3.9:1, below the
          // floor). Same mapping as mobile's dueToneColor: overdue
          // danger, today the accent, soon ink, later muted.
          const toneClass =
            due.tone === "overdue"
              ? "text-danger"
              : due.tone === "today"
                ? "text-blue"
                : due.tone === "soon"
                  ? "text-ink"
                  : "text-muted";
          return (
            <li
              key={n.itemId}
              className="flex items-baseline gap-2 text-sm"
              data-testid={`next-due-row-${n.title}`}
            >
              <span
                className={`shrink-0 text-sm font-medium ${toneClass}`}
                data-testid={`next-due-label-${n.title}`}
                data-tone={due.tone}
              >
                {due.label}
              </span>
              <Link
                to={`/app/list/${n.listId}`}
                className="text-ink hover:underline"
              >
                {n.title}
              </Link>
              <span className="text-xs text-muted">{n.listName}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function SignOutButton() {
  const { signOut } = useAuthActions();
  return (
    <button
      type="button"
      data-testid="signout"
      onClick={() => void signOut()}
      className="whitespace-nowrap rounded-lg border border-line bg-page px-3 py-1.5 text-sm font-medium text-muted shadow-sm hover:bg-pressed"
    >
      Sign out
    </button>
  );
}

/**
 * Global search: list names + item titles across every list the viewer
 * belongs to (membership-scoped server-side in convex/search.ts).
 * Debounced 200ms so a word a minute doesn't hammer the search, results
 * grouped Lists / Items, each row links into its list. find is a
 * MUTATION (#654 — a query cannot spend the search budget), so results
 * arrive per settled term instead of reactively; the seq guard keeps a
 * slow earlier response from overwriting a newer term's.
 */
function GlobalSearch() {
  const [raw, setRaw] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setTerm(raw.trim()), 200);
    return () => clearTimeout(t);
  }, [raw]);
  const searchFind = useMutation(api.search.find);
  const [results, setResults] = useState<
    Awaited<ReturnType<typeof searchFind>> | undefined
  >(undefined);
  const [searchError, setSearchError] = useState<string | null>(null);
  const seq = useRef(0);
  useEffect(() => {
    if (term === "") {
      setResults(undefined);
      setSearchError(null);
      return;
    }
    const mine = ++seq.current;
    searchFind({ q: term })
      .then((r) => {
        if (mine === seq.current) {
          setResults(r);
          setSearchError(null);
        }
      })
      .catch((err) => {
        // A tripped search cap lands here — the server's friendly copy
        // renders in the panel, never a crash.
        if (mine === seq.current) {
          setResults(undefined);
          setSearchError(errorMessage(err, GENERIC_ERROR_MESSAGE));
        }
      });
  }, [term, searchFind]);

  const empty =
    term !== "" && results !== undefined && results.lists.length === 0 && results.items.length === 0;

  return (
    <div className="mt-8">
      <input
        data-testid="global-search-input"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        placeholder="Search your lists and items…"
        className="w-full rounded-lg border border-line bg-page px-3 py-2 text-sm text-ink placeholder:text-faint shadow-sm focus:border-blue focus:outline-none"
      />
      {/* Visibility keys off the raw input so clearing the box hides
          the panel immediately; only the QUERY waits for the debounce. */}
      {raw.trim() !== "" && (
        <div
          className="mt-2 rounded-xl border border-line bg-page p-4 shadow-sm"
          data-testid="search-results"
        >
          {searchError !== null ? (
            <p className="text-sm text-muted" data-testid="search-error">
              {searchError}
            </p>
          ) : empty ? (
            <p className="text-sm text-muted" data-testid="search-empty">
              No matches for “{term}”.
            </p>
          ) : (
            <>
              {results !== undefined && results.lists.length > 0 && (
                <section>
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">
                    Lists
                  </h2>
                  <ul className="mt-1 space-y-1">
                    {results.lists.map((l) => (
                      <li key={l.listId}>
                        <Link
                          to={`/app/list/${l.listId}`}
                          className="text-sm font-medium text-ink hover:underline"
                          data-testid={`search-list-${l.name}`}
                        >
                          {l.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {results !== undefined && results.items.length > 0 && (
                <section className="mt-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">
                    Items
                  </h2>
                  <ul className="mt-1 space-y-1">
                    {results.items.map((i) => (
                      <li key={i.itemId} className="flex items-baseline gap-2">
                        <Link
                          to={`/app/list/${i.listId}`}
                          className={`text-sm hover:underline ${i.done ? "text-muted line-through" : "text-ink"}`}
                          data-testid={`search-item-${i.title}`}
                        >
                          {i.title}
                        </Link>
                        <span className="text-xs text-muted">{i.listName}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// List summaries come from @todotogether/shared — the mobile Lists screen
// (#21) consumes the same shapes, so the clients can't drift.
type OwnedList = ListSummary;
type JoinedList = JoinedListSummary;

/** A myLists row tagged with its own ownership, so section membership
 * (the classifier's input) and the row menu (the owner role) both read
 * off the row and never off the section it happens to render in. */
type ListRow = (OwnedList | JoinedList) & { ownedByCaller: boolean };

/**
 * The two membership sections (#754), regrouped from the server's
 * owned/joined arrays through the ONE classifier home (listScope,
 * #753): personal iff the caller owns the list and its live
 * memberCount is exactly 1, shared otherwise. Within Shared the
 * caller's own shared lists render above the joined ones (the filter
 * order over owned-then-joined rows); starredFirst still pins starred
 * rows inside each section. The split is pure over the props — the
 * reactive myLists query re-runs it on every membership change, which
 * is what moves a row between sections live.
 */
function ListSections({
  owned,
  joined,
}: {
  owned: OwnedList[];
  joined: Array<OwnedList | JoinedList>;
}) {
  const rows: ListRow[] = [
    ...owned.map((list) => ({ ...list, ownedByCaller: true })),
    ...joined.map((list) => ({ ...list, ownedByCaller: false })),
  ];
  return (
    <>
      <ListsSection
        testIdPrefix="personal"
        title="Personal"
        emptyText="No personal lists yet. Create one above."
        lists={rows.filter((list) => listScope(list) === "personal")}
      />
      <ListsSection
        testIdPrefix="shared"
        title="Shared"
        emptyText="Lists shared with other people will appear here."
        lists={rows.filter((list) => listScope(list) === "shared")}
      />
    </>
  );
}

function CreateList() {
  const create = useMutation(api.lists.create);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  // The paywall surface (#18) lives exactly at the bite: the free-plan
  // refusal is the only place short of Settings that offers the upgrade —
  // no banners, no counters, no nagging anywhere else. (Convex wraps
  // rejection messages in dev frames, so match the pinned copy anywhere.)
  const quotaRefusal = error !== null && error.includes("Free plan limit");

  return (
    <div className="mt-8">
      <form
        className="flex gap-2"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          // Pre-validated with the shared maxima: the friendly copy lands
          // instantly, before anything reaches the server (#107/#110).
          const trimmed = name.trim();
          if (trimmed === "") {
            setError("List name can't be empty");
            return;
          }
          if (trimmed.length > LIST_NAME_MAX) {
            setError(`List name too long (max ${LIST_NAME_MAX})`);
            return;
          }
          try {
            await create({ name: capitaliseFirstLetter(trimmed) });
            setName("");
          } catch (err) {
            setError(errorMessage(err, GENERIC_ERROR_MESSAGE));
          }
        }}
      >
        <input
          data-testid="new-list-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New list name"
          autoCapitalize="sentences"
          spellCheck
          className="flex-1 rounded-lg border border-line bg-page px-3 py-2 text-sm text-ink placeholder:text-faint shadow-sm focus:border-blue focus:outline-none"
        />
        <button
          type="submit"
          data-testid="create-list"
          className="rounded-lg bg-blue-fill px-4 py-2 text-sm font-medium text-on-blue shadow-sm hover:bg-blue-fill-pressed"
        >
          Create list
        </button>
      </form>
      {error && (
        <p
          className="mt-2 rounded-lg border border-danger/40 bg-page px-3 py-2 text-sm text-danger"
          data-testid="create-list-error"
        >
          {error}
        </p>
      )}
      {quotaRefusal && (
        <p className="mt-1 text-sm text-muted">
          <Link
            to="/app/settings"
            className="font-medium text-ink underline hover:text-muted"
            data-testid="paywall-upsell"
          >
            {SEE_PLANS_LINK_LABEL}
          </Link>
        </p>
      )}
    </div>
  );
}

function ListsSection({
  testIdPrefix,
  title,
  emptyText,
  lists,
}: {
  testIdPrefix: string;
  title: string;
  emptyText: string;
  lists: ListRow[];
}) {
  // Starred Lists pin above unstarred ones within this section (#249):
  // the SHARED starredFirst stable partition over the caller's own
  // stamps (the server already returns them, #70) — each group keeps
  // its existing order, so unstarring restores exactly the previous
  // arrangement. Surface only; no server change.
  const ordered = starredFirst(lists);
  return (
    <section className="mt-8">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
        {title}
      </h2>
      {lists.length === 0 ? (
        <p
          className="mt-2 rounded-xl border border-dashed border-line bg-page p-6 text-center text-sm text-muted"
          data-testid={`empty-${testIdPrefix}`}
        >
          {emptyText}
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-line rounded-xl border border-line bg-page shadow-sm">
          {ordered.map((list) => {
            // The progress fill (#271): completed ÷ total of LIVE items,
            // the shared fraction — null (no live items) renders NO fill
            // at all, so "0 of 0" can never read as full.
            const fraction = progressFraction(list);
            return (
              <li
                key={list._id}
                className="relative flex items-center justify-between gap-3 overflow-hidden px-4 py-3"
                data-testid={`${testIdPrefix}-list-${list.name}`}
              >
                {/* A quiet stone tint behind the row's content, growing
                    from the left edge. Transform-scaled (cheap, no
                    layout) and transitioned subtly; reduced-motion
                    disables the transition, never the state. */}
                {fraction !== null && (
                  <div
                    aria-hidden="true"
                    data-testid={`progress-fill-${list.name}`}
                    className="absolute inset-y-0 left-0 w-full origin-left bg-wash transition-transform duration-500 motion-reduce:transition-none"
                    style={{ transform: `scaleX(${fraction})` }}
                  />
                )}
                <span className="relative flex min-w-0 items-center gap-2">
                  <Link
                    to={`/app/list/${list._id}`}
                    className={`truncate hover:underline ${
                      list.starredAt !== undefined
                        ? "font-bold text-ink"
                        : "font-medium text-ink"
                    }`}
                    data-testid={`${testIdPrefix}-link-${list.name}`}
                  >
                    {list.name}
                  </Link>
                  {/* The count beside the fill (#271): the fraction in
                      words, so the encoding is never colour-alone. */}
                  {fraction !== null && (
                    <span
                      className="shrink-0 text-xs text-muted"
                      data-testid={`progress-count-${list.name}`}
                    >
                      {list.completedCount} of {list.totalCount}
                    </span>
                  )}
                  {/* The star pill (#249): a FILLED glyph in the row's
                      pill voice (the assignee chip's shape) — the same
                      visual law as mobile. The path is mobile's star
                      geometry, verbatim: one star, two renderers. */}
                  {list.starredAt !== undefined && (
                    <span
                      className="relative inline-flex shrink-0 items-center rounded-full border border-line bg-wash px-1.5 py-0.5"
                      data-testid={`star-pill-${list.name}`}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="h-3 w-3 fill-blue"
                      >
                        <path d="M12 3.6l2.5 5.1 5.6.8-4 4 .9 5.6-5-2.7-5 2.7.9-5.6-4-4 5.6-.8L12 3.6Z" />
                      </svg>
                    </span>
                  )}
                </span>
                <span className="relative">
                  <ListRowActions
                    listId={list._id as Id<"lists">}
                    listName={list.name}
                    starred={list.starredAt !== undefined}
                    canManage={list.ownedByCaller}
                  />
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function ListRowActions({
  listId,
  listName,
  starred,
  canManage,
}: {
  listId: Id<"lists">;
  listName: string;
  /** The CALLER's own star stamp (#70) — drives Star/Unstar (#249). */
  starred: boolean;
  canManage: boolean;
}) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(listName);
  const [actionError, setActionError] = useState<string | null>(null);
  const rename = useMutation(api.lists.rename);
  const softDelete = useMutation(api.lists.softDelete);
  // The star is the Member's private view (#70), so it renders at ANY
  // Role — Viewer included — exactly like the mobile menu row (#249).
  const setStarred = useMutation(api.lists.setStarred);

  if (renaming) {
    return (
      <form
        className="flex items-center gap-2"
        onSubmit={async (e) => {
          e.preventDefault();
          setActionError(null);
          // Deliberately unguarded: the rename form exercises the server
          // refusal path (the row slot renders it while the form is open);
          // only the hot create/add paths pre-validate.
          try {
            await rename({ listId, name: capitaliseFirstLetter(name.trim()) });
            setRenaming(false);
          } catch (err) {
            setActionError(errorMessage(err, GENERIC_ERROR_MESSAGE));
          }
        }}
      >
        <input
          data-testid="rename-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoCapitalize="sentences"
          spellCheck
          className="rounded border border-line px-2 py-1 text-sm"
        />
        <button
          type="submit"
          data-testid="rename-save"
          className="text-sm text-muted underline"
        >
          Save
        </button>
        {/* A failed rename keeps the form open — the refusal must render
            right here, not only in the closed-row branch below. */}
        {actionError && (
          <span className="text-xs text-danger" data-testid="row-action-error">
            {actionError}
          </span>
        )}
      </form>
    );
  }

  return (
    <span className="flex items-center gap-3 text-sm">
      <button
        type="button"
        data-testid={starred ? `unstar-list-${listName}` : `star-list-${listName}`}
        onClick={() => {
          setActionError(null);
          setStarred({ listId, starred: !starred }).catch((err) =>
            setActionError(errorMessage(err, GENERIC_ERROR_MESSAGE)),
          );
        }}
        className="text-muted hover:text-ink hover:underline"
      >
        {starred ? "Unstar" : "Star"}
      </button>
      {canManage && (
        <>
          <button
            type="button"
            data-testid="rename-start"
            onClick={() => setRenaming(true)}
            className="text-muted hover:text-ink hover:underline"
          >
            Rename
          </button>
          <button
            type="button"
            data-testid="delete-list"
            onClick={() => {
              setActionError(null);
              softDelete({ listId }).catch((err) =>
                setActionError(errorMessage(err, GENERIC_ERROR_MESSAGE)),
              );
            }}
            className="text-danger hover:underline"
          >
            Delete
          </button>
        </>
      )}
      {actionError && (
        <span className="text-xs text-danger" data-testid="row-action-error">
          {actionError}
        </span>
      )}
    </span>
  );
}

/**
 * The Owner's bin for soft-deleted Lists. Restore is a Paid capability
 * server-side (ticket #71), so the button only renders for Paid Users —
 * Free Users get the upsell in its place, pointing at Settings where
 * Upgrade lives (the same words as the server's refusal message).
 */
// Recently-deleted bin (ticket #71/#72 surface).
function DeletedBin({
  lists,
  tier,
}: {
  lists: Array<{ _id: string; name: string; restoreDeadline: number }>;
  tier: Tier | undefined;
}) {
  const restore = useMutation(api.lists.restore);
  const [error, setError] = useState<string | null>(null);
  return (
    <section className="mt-8 rounded-xl border border-warn/40 bg-page p-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-warn">
        Recently deleted — restorable for 30 days
      </h2>
      {error && (
        <p className="mt-2 text-xs text-danger" data-testid="restore-error">
          {error}
        </p>
      )}
      <ul className="mt-2 space-y-1">
        {lists.map((l) => (
          <li key={l._id} className="flex items-center justify-between text-sm">
            <span className="text-muted" data-testid={`deleted-list-${l.name}`}>
              {l.name}
            </span>
            {tier === "paid" ? (
              <button
                type="button"
                data-testid={`restore-${l.name}`}
                onClick={() => {
                  setError(null);
                  restore({ listId: l._id as Id<"lists"> }).catch((err) =>
                    setError(
                      errorMessage(err, GENERIC_ERROR_MESSAGE),
                    ),
                  );
                }}
                className="text-warn underline"
              >
                Restore
              </button>
            ) : tier === "free" ? (
              <Link
                to="/app/settings"
                data-testid={`restore-upsell-${l.name}`}
                title={PAID_RESTORE_MESSAGE}
                className="text-xs text-warn underline"
              >
                {PAID_RESTORE_MESSAGE}
              </Link>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
